import { NgClass } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  OnInit,
  Signal,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RevealDetailsComponent } from '@app/shared/components/reveal-details/reveal-details.component';
import { DialogComponent } from '@app/shared/dialog/dialog-component.directive';
import {
  Command,
  CommandResult,
  CommandSection,
  CommandStatusbar,
  EnhancedCommandResultEntry,
  QuickCommandService,
  SortOrderCommandService,
  TranslatedCommandResultEntry,
} from '@app/shared/quick-commands';
import { QuickCommandNameComponent } from '@app/shared/quick-commands/components/quick-command-name/quick-command-name.component';
import { HotkeyService } from '@app/shared/services/hotkey.service';
import { RecentlyUsedEntry } from '@app/shared/services/recently-used.service';
import { SharedModule } from '@app/shared/shared.module';
import { isTranslation, Translatable } from '@app/translate/models/translatable';
import { PortalTranslateService } from '@app/translate/services/portal-translate.service';
import { debounce, groupBy, sortBy, uniqBy } from '@app/utils/common';
import { equals } from '@app/utils/equals';
import { PortalError } from '@app/utils/error/portal-error';
import { loggerFor } from '@app/utils/logger';
import { useResettingSignal } from '@app/utils/signals/use-resetting-signal';
import { isNotBlank } from '@app/utils/string';
import produce from 'immer';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'portal-quick-commands-dialog',
  standalone: true,
  imports: [FormsModule, NgClass, SharedModule, RevealDetailsComponent, QuickCommandNameComponent],
  templateUrl: './quick-commands-dialog.component.html',
})
export class QuickCommandsDialogComponent extends DialogComponent<void, void> implements OnInit {
  inputElement = viewChild.required<ElementRef<HTMLInputElement>>('input');

  allEntries = signal<EnhancedCommandResultEntry[]>([]);
  translatedEntries = computed(() => this.allEntries().map(this.translate));
  entries = signal<TranslatedCommandResultEntry[]>([]);
  indexedEntries: Signal<IndexedResult[]>;

  activeCommand = signal<Command | null>(null);
  active = signal(0);
  alwaysShowSections = signal(false);
  busy = signal(false);
  commandUsage: Signal<Record<string, RecentlyUsedEntry>>;
  currentGroup = signal<string | null>(null);
  emptyMessage = signal<Translatable | null>(null);
  error = signal<PortalError | null>(null);
  errorMessage = signal<Translatable | null>(null);
  highlightResults = signal(true);
  infoMessage = signal<Translatable | null>(null);
  lastCommand: Signal<Command>;
  placeholder = signal<Translatable | null>(null);
  query = signal('');
  readonly = signal<boolean>(false);
  resultSections = signal<CommandSection[]>([]);
  sections: Signal<Section[]>;
  selectedResults = signal<IndexedResult[]>([]);
  showingHistory = signal<boolean>(false);
  sort = signal<false | 'asc' | 'desc'>(false);
  statusbar: WritableSignal<CommandStatusbar>;
  statusbarTranslation = computed((): Translatable => {
    if (isTranslation(this.statusbar().translationKey)) {
      return this.statusbar().translationKey;
    }

    return {
      key: this.statusbar().translationKey as string,
      parameters: this.statusbar().parameters,
    };
  });
  warningMessage = signal<Translatable | null>(null);

  readonly HistorySection = QuickCommandService.HistorySection;

  private commands: Command[] = [];
  private commandSections: CommandSection[] = [];
  private fallbackCommands: Command[] = [];
  private debouncedUpdate = debounce(() => this.updateFilter(true), 400);
  private ignoreMouseEvents = useResettingSignal(false, 500);
  private lastResults: EnhancedCommandResult[] = [];
  private lastSubscription: Subscription | null = null;
  private log = loggerFor('QuickCommandsDialog');

  /**
   * We remember if the user has just executed a command because if not
   * (it is possible for commands to emit more than one event) then we
   * will not reset the active index (i.e. the currently focused element).
   */
  private userInitiated: boolean = false;

  private readonly DefaultStatusbar: CommandStatusbar = {
    hideDefaultHint: true,
    translationKey: 'quickCommands.hint',
  };

  private mapCommand = (command: Command): TranslatedCommandResultEntry => {
    return {
      command,
      historyId: command.name,
      icon: command.icon,
      label: this.portalTranslateService.translate(command.label),
      section: command.section,
      value: null,
    };
  };

  private translate = (entry: IndexedResult): IndexedResult => {
    return {
      ...entry,
      group: entry.group,
      label: this.portalTranslateService.translate(entry.label, entry.labelParams),
    };
  };

  private get lastResult(): EnhancedCommandResult | null {
    return this.lastResults.at(-1) ?? null;
  }

  constructor(
    private destroyRef: DestroyRef,
    private hotkeyService: HotkeyService,
    private portalTranslateService: PortalTranslateService,
    private quickCommandService: QuickCommandService,
    private sortOrderCommandService: SortOrderCommandService
  ) {
    super();

    this.commandUsage = toSignal(this.sortOrderCommandService.commandUsage$, {
      initialValue: {},
    });

    this.lastCommand = computed<Command | null>(() => {
      const selectedCommands = this.selectedResults()
        .map((result) => result.command)
        .filter((command) => command != null);

      return selectedCommands.at(-1) ?? null;
    });

    this.sections = computed((): Section[] => {
      let sections = this.resultSections();
      let index = 0;

      const groupedBySection = groupBy(this.entries(), (entry) => entry.section ?? 'others');

      return sections
        .map((sectionDefinition): Section | null => {
          if (
            groupedBySection[sectionDefinition.name] == null ||
            groupedBySection[sectionDefinition.name].length === 0
          ) {
            return null;
          }

          const section = {
            groups: this.getGroups(
              sectionDefinition,
              groupedBySection[sectionDefinition.name] ?? [],
              index,
              this.sort()
            ),
            label: sectionDefinition.label,
            name: sectionDefinition.name,
          };

          index += groupedBySection[sectionDefinition.name].length;

          return section;
        })
        .filter((section) => section != null) as Section[];
    });

    this.indexedEntries = computed(() =>
      this.sections()
        .flatMap((section) => section.groups)
        .flatMap((group) => group.entries)
    );

    this.statusbar = signal<CommandStatusbar>(this.DefaultStatusbar);

    // Needs access to the angular injector
    this.handleHotkeys();
  }

  ngOnInit(): void {
    this.hotkeyService
      .addShortcut({
        keys: 'escape',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.dismiss();
      });

    this.quickCommandService.commands$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ({ commands, sections }) => {
        const { fallback = [], normal = [] } = groupBy(commands, (command) =>
          command.fallback ? 'fallback' : 'normal'
        );

        this.commands = normal;
        this.fallbackCommands = fallback;
        this.commandSections = sections;

        this.resetCommands();
        this.updateFilter(false);
      },
    });

    this.ignoreMouseEvents.set(true);
  }

  clearCommand(): void {
    const firstResult = this.selectedResults()[0];

    // Removing the first result will also remove all results after it
    this.removeResult(firstResult, false);
    this.inputElement().nativeElement.focus();
  }

  deleteActive(): void {
    const activeIndex = this.active();
    const resultToRemove = this.indexedEntries().find((entry) => entry.index === activeIndex);

    if (this.showingHistory() && resultToRemove?.historyId != null) {
      this.quickCommandService.removeHistory(
        this.lastCommand(),
        this.selectedResults().slice(1),
        resultToRemove
      );

      this.executeCommand(this.lastCommand(), {
        chosenEntry: null,
        ignoreMouseEvents: true,
        query: this.query(),
      });
    }
  }

  goToResult(index: number): void {
    const resultToRemove = this.selectedResults()[index + 1];

    if (resultToRemove != null) {
      this.removeResult(resultToRemove, false);
    }
  }

  onAltLeft(): void {
    if (this.activeCommand() != null) {
      this.removeLatestResult();
    }
  }

  onClick(index: number, result: IndexedResult): void {
    if (this.readonly() === false) {
      this.executeCommandAtIndex(index, result, false);
      this.inputElement().nativeElement.focus();
    }
  }

  onEnter(index: number) {
    if (this.readonly() === false && this.busy() === false) {
      const result = this.indexedEntries().find((entry) => entry.index === index);
      this.executeCommandAtIndex(index, result, true);
    }
  }

  onInput(): void {
    if (this.lastResult?.debounce) {
      if (isNotBlank(this.query())) {
        this.busy.set(true);
      }

      this.debouncedUpdate();
    } else {
      this.updateFilter(true);
    }
  }

  setFocus(index: number): void {
    if (this.ignoreMouseEvents() === false) {
      this.active.set(index);
    }
  }

  shiftFocus(shiftBy: number, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const numberOfResults = this.indexedEntries().length;

    // Go up (i.e. select the previous result)
    if (shiftBy < 0 && this.active() === 0) {
      // Select the last result
      this.active.set(numberOfResults - 1);
    } else if (shiftBy > 0 && this.active() === numberOfResults - 1) {
      // Select the first result
      this.active.set(0);
    } else {
      this.active.update((active) => {
        let updatedIndex = active + shiftBy;

        if (updatedIndex >= numberOfResults) {
          updatedIndex = numberOfResults - 1;
        } else if (updatedIndex < 0) {
          updatedIndex = 0;
        }

        return updatedIndex;
      });
    }

    this.scrollActiveElementIntoView();
  }

  updateFilter(ignoreMouseEvents: boolean): void {
    if (this.activeCommand() == null) {
      this.resetCommands();
    } else {
      this.executeCommand(this.activeCommand(), {
        ignoreMouseEvents,
        query: this.query(),
      });
    }
  }

  private executeCommandAtIndex(index: number, result: IndexedResult, ignoreMouseEvents: boolean): void {
    if (this.activeCommand() != null) {
      this.executeCommand(this.activeCommand(), {
        chosenEntry: result,
        ignoreMouseEvents,
        query: this.query(),
      });
    } else {
      const command = result.command ?? this.getCommandAtIndex(index);

      if (command.fallback) {
        this.executeCommand(command, {
          chosenEntry: result,
          ignoreMouseEvents,
          query: this.query(),
        });
      } else {
        this.executeCommand(command, {
          ignoreMouseEvents,
        });
      }
    }
  }

  private executeCommand(command: Command, options: CommandOptions): void {
    const { chosenEntry = null, query = '' } = options ?? {};
    const trimmedQuery = query.trim();
    const commandEntry = this.translatedEntries().find((entry) => entry.command === command);

    this.userInitiated = true;
    this.busy.set(true);
    this.error.set(null);

    if (this.lastSubscription != null) {
      this.lastSubscription.unsubscribe();
      this.lastSubscription = null;
    }

    // Remember commands
    if (this.lastCommand() == null) {
      this.sortOrderCommandService.remember(command);
    }

    const useAutomaticSearch = chosenEntry == null && this.lastResult?.filterAutomatically;
    const hasResult = this.lastResult != null;
    const runAutomaticSearch = useAutomaticSearch && hasResult && options.refresh !== true;

    const result$ = runAutomaticSearch
      ? this.runAutomaticSearch(trimmedQuery)
      : this.runManualSearch(chosenEntry, command, trimmedQuery);

    this.lastSubscription = result$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      error: (error) => {
        console.error(error);

        this.allEntries.set([]);
        this.busy.set(false);
        this.entries.set([]);
        this.error.set(error);
      },
      next: (result) => {
        this.log.info('Received new result', result);

        this.busy.set(false);

        if (result.type === 'close') {
          this.close();
        } else if (result.type === 'return') {
          this.removeLatestResult();

          if (result.refresh) {
            this.executeCommand(this.activeCommand(), {
              ignoreMouseEvents: true,
              refresh: true,
            });
          }
        } else {
          const firstCommand = this.activeCommand() == null;

          this.setResults(result, command, options.ignoreMouseEvents, this.userInitiated === true);

          if (firstCommand) {
            this.selectedResults.set([commandEntry]);
            this.query.set('');
          } else if (chosenEntry != null) {
            if (result.replace === false) {
              this.selectedResults.update((entries) => [...entries, chosenEntry]);
            }

            this.query.set('');
          }
        }

        this.userInitiated = false;
      },
    });
  }

  private getGroups(
    section: CommandSection,
    entries: TranslatedCommandResultEntry[],
    start: number,
    sort: false | 'asc' | 'desc'
  ): Group[] {
    if (section.group === false) {
      return [
        {
          entries: toSorted(entries).map((entry, index) => {
            return {
              ...entry,
              index: start + index,
            };
          }),
        },
      ];
    }

    const groups: Group[] = [];
    const grouped = groupBy(entries, (entry) => entry.group ?? '');

    Object.entries(grouped).forEach(([label, entries]) => {
      groups.push({
        entries: toSorted(entries).map((entry, index) => {
          return {
            ...entry,
            index: start + index,
          };
        }),
        label,
      });

      start += entries.length;
    });

    return groups;

    function toSorted(list: TranslatedCommandResultEntry[]): TranslatedCommandResultEntry[] {
      if (sort === false) {
        return list;
      } else {
        return list.slice().sort(
          sortBy([
            {
              key: (item) => item.label,
              order: sort,
            },
          ])
        );
      }
    }
  }

  private handleHotkeys(): void {
    let subscriptions: Function[] = [];

    effect(() => {
      subscriptions.forEach((dispose) => dispose());

      const commands = uniqBy(
        this.allEntries().filter((entry) => entry.command?.hotkey != null),
        (entry) => entry.command.hotkey
      );

      subscriptions = commands.map((entry) => this.registerHotkey(entry.command!));
    });
  }

  private getCommandAtIndex(index: number): Command {
    const entry = this.entries()[index];

    if (entry.command?.fallback) {
      return this.fallbackCommands.find((command) => command === entry.command);
    }

    return this.commands.find((command) => command === entry.command);
  }

  private mapCommands(commands: Command[]): TranslatedCommandResultEntry[] {
    const div = document.createElement('div');
    const filteredResults = commands.map(this.mapCommand).filter((result) => {
      div.innerHTML = result.label;
      return div.innerText.toLowerCase().includes(this.query().toLowerCase().trim());
    });

    const grouped = groupBy(
      filteredResults,
      (command) => command.section ?? 'others',
      (commands) => this.sortOrderCommandService.sortCommands(commands)
    );

    return this.commandSections.flatMap((section) => grouped[section.name] ?? []);
  }

  private registerHotkey(command: Command): () => void {
    const subscription = this.hotkeyService
      .addShortcut({
        keys: command.hotkey!,
        prevent: true,
        stop: true,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.executeCommand(command, {
          ignoreMouseEvents: true,
        });
      });

    return () => {
      subscription.unsubscribe();
    };
  }

  private removeLatestResult(): void {
    this.removeResult(this.selectedResults()[this.selectedResults().length - 1], true);
  }

  private removeResult(result: IndexedResult, ignoreMouseEvents: boolean): void {
    if (this.lastSubscription != null) {
      this.lastSubscription.unsubscribe();
      this.lastSubscription = null;
    }

    const index = this.selectedResults().indexOf(result);
    const updatedResults = this.lastResults.slice(0, index);
    const lastResult = updatedResults.at(-1);

    this.query.set('');
    this.selectedResults.set(this.selectedResults().slice(0, index));
    this.lastResults = updatedResults;

    if (updatedResults.length === 0) {
      this.activeCommand.set(null);
      this.updateFilter(ignoreMouseEvents);
      this.active.set(
        this.indexedEntries().find((entry) => entry.command?.name === result.command?.name)?.index ?? 0
      );
    } else {
      this.setResults(lastResult, lastResult.command, ignoreMouseEvents, true);

      // Reset the filter
      this.updateFilter(ignoreMouseEvents);

      // Find the previously selected result after we've updated the filter (the index may no longer be correct).
      const previouslyActiveResult = this.indexedEntries().find((entry) => equals(entry.value, result.value));

      if (previouslyActiveResult != null) {
        this.active.set(previouslyActiveResult.index);
      }

      requestAnimationFrame(() => this.scrollActiveElementIntoView('center'));
    }
  }

  private resetCommands(active?: number): void {
    const commands = this.mapCommands(this.commands);

    this.allEntries.set(commands);
    this.busy.set(false);
    this.emptyMessage.set(null);
    this.error.set(null);
    this.errorMessage.set(null);
    this.ignoreMouseEvents.set(true);
    this.infoMessage.set(null);
    this.placeholder.set(null);
    this.readonly.set(false);
    this.resultSections.set(this.commandSections);
    this.warningMessage.set(null);
    this.statusbar.set({
      hideDefaultHint: true,
      translationKey: 'quickCommands.hint',
    });

    if (this.query()?.length > 0 && commands.length === 0) {
      this.runFallbackCommands();
    } else {
      this.active.set(active ?? 0);
      this.entries.set(commands);
    }
  }

  private runAutomaticSearch(query: string): Observable<CommandResult<unknown>> {
    const lastResult = this.lastResult;

    this.allEntries.set(lastResult.results);

    const searchString = query.toLocaleLowerCase();
    const result = produce(lastResult, (draft) => {
      draft.results = this.translatedEntries().filter((entry) =>
        entry.label.toLocaleLowerCase().includes(searchString)
      );

      const filtered = draft.results.length;
      const total = lastResult.results.length;

      if (draft.statusbar?.translationKey != null && filtered !== total) {
        draft.statusbar.parameters = {
          ...draft.statusbar.parameters,
          filtered: draft.results.length,
          total: this.allEntries().length,
        };

        draft.statusbar.translationKey = `${draft.statusbar.translationKey}.filtered`;
      }
    });

    return of(result);
  }

  private runFallbackCommands(): void {
    if (this.fallbackCommands.length === 0) {
      this.active.set(0);
      this.allEntries.set([]);
      this.entries.set([]);
    } else {
      const commandResults$ = this.fallbackCommands.map((command) =>
        this.quickCommandService
          .call$(command, {
            query: this.query(),
            results: [],
            remember: false,
          })
          .pipe(
            tap((result) => {
              result.results.forEach((result) => {
                result.command = command;
              });
            })
          )
      );

      combineLatest(commandResults$)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((results) => {
          this.active.set(0);
          this.allEntries.set(results.flatMap((values) => values.results));
          this.entries.set(this.translatedEntries());
        });
    }
  }

  private runManualSearch(
    chosenEntry: IndexedResult,
    command: Command,
    query: string
  ): Observable<CommandResult<unknown>> {
    const [, ...resultsWithoutCommand] = this.selectedResults();
    const activeResults = resultsWithoutCommand;

    if (chosenEntry != null) {
      // Fallback commands need the query
      if (command.fallback === false) {
        query = '';
      }

      activeResults.push(chosenEntry);
    }

    return this.quickCommandService
      .call$(command, {
        query,
        remember: (this.lastResult?.rememberResult ?? false) && chosenEntry != null,
        results: activeResults,
      })
      .pipe(
        tap((result) => {
          let currentIndex = this.selectedResults().length - 1;

          if (currentIndex < 0) {
            currentIndex = 0;
          }

          if (chosenEntry != null) {
            ++currentIndex;
          }

          this.lastResults[currentIndex] = {
            ...result,
            command,
          };
        })
      );
  }

  private scrollActiveElementIntoView(position: ScrollLogicalPosition = 'nearest'): void {
    this.ignoreMouseEvents.set(true);
    document.getElementById(`result-${this.active()}`)?.scrollIntoView({
      block: position,
    });
    requestAnimationFrame(() => this.ignoreMouseEvents.set(false));
  }

  private setResults(
    result: CommandResult<unknown>,
    command: Command,
    ignoreMouseEvents: boolean,
    resetActive: boolean
  ): void {
    this.allEntries.set(result.results);
    this.activeCommand.set(command);
    this.entries.set(this.translatedEntries());

    if (resetActive) {
      this.active.set(0);
    }

    this.alwaysShowSections.set(command.alwaysShowSections);
    this.currentGroup.set(result.currentGroup);
    this.emptyMessage.set(result.emptyMessage);
    this.error.set(null);
    this.errorMessage.set(result.errorMessage);
    this.highlightResults.set(result.highlight);
    this.infoMessage.set(result.infoMessage);
    this.placeholder.set(result.placeholder);
    this.readonly.set(result.readonly);
    this.resultSections.set(result.sections);
    this.showingHistory.set(result.showHistory);
    this.sort.set(result.sort ?? false);
    this.statusbar.set({
      hideDefaultHint: false,
      translationKey: '',
      ...result.statusbar,
    });
    this.warningMessage.set(result.warningMessage);

    if (ignoreMouseEvents) {
      this.ignoreMouseEvents.set(true);
    }
  }
}

interface EnhancedCommandResult extends CommandResult<unknown> {
  command?: Command;
}

interface IndexedResult extends TranslatedCommandResultEntry {
  index: number;
}

interface CommandOptions {
  /**
   * One of the result entries that is passed to the command.
   */
  chosenEntry?: IndexedResult;

  /**
   * Ignore mouse events while the command is active.
   */
  ignoreMouseEvents: boolean;

  /**
   * Query string that is passed to the command.
   */
  query?: string;

  /**
   * Ignore the last result that was remembered and run the command again.
   */
  refresh?: boolean;
}

interface Group {
  label?: string;
  entries: IndexedResult[];
}

interface Section {
  className?: string;
  groups: Group[];
  label?: string;
  name: string;
}

interface SetResultsOptions {
  ignoreMouseEvents: boolean;
  resetActive: boolean;
}
